import {
  LegacyProductCode,
  ProductBehavior,
} from '@up2stay/up2promo-api-client';
import type { ResolveProductResponseDto } from '@up2stay/up2promo-api-client';

import {
  ensureSetupPathHasProductType,
  mergeSetupProductType,
} from '@/core/lib/build-products-setup-path';
import {
  classifyNativeLink,
  parseProfileViewerUrl,
} from '@/core/lib/native-deep-link';
import type { ProductHashResolution } from '@/core/lib/product-hash-resolution';
import { parseSetupTypeParam } from '@/core/lib/product-setup-type';

export type LinkResolveKind =
  | 'profile_card'
  | 'owned_product'
  | 'teammate_product'
  | 'needs_setup'
  | 'external'
  | 'visitor_destination'
  | 'app_path'
  | 'error_unavailable'
  | 'error_generic';

export type LinkResolveDestination = {
  kind: LinkResolveKind;
  hash: string | null;
  targetUrl: string | null;
  mappingType: ProductBehavior | null;
  /** Parsed product kind — prefer api-client enum. */
  productType: LegacyProductCode | null;
  profile?: {
    workspaceUsername: string;
    username: string;
  };
  productId?: string;
  appPath?: string;
};

export type ClassifyLinkResolveInput = {
  hash: string | null;
  resolution: ResolveProductResponseDto | null;
  ownership?: ProductHashResolution | null;
  /**
   * QR / deep-link `?t=` hint. Used when resolve/ownership lack a type;
   * DB/ownership still win when present.
   */
  hintProductType?: LegacyProductCode | string | null;
  /** Direct profile UL without hash resolve. */
  directProfile?: {
    workspaceUsername: string;
    username: string;
    profileUrl?: string | null;
  } | null;
};

function isProfileScan(
  mappingType: ProductBehavior | null | undefined,
  productType: LegacyProductCode | string | null | undefined,
): boolean {
  return (
    mappingType === ProductBehavior.PROFILE ||
    parseSetupTypeParam(
      productType == null ? undefined : String(productType),
    ) === LegacyProductCode.BC
  );
}

function isSetupFrontendUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase();
    const segments = parsed.pathname.split('/').filter(Boolean);
    if (segments.length !== 1) return false;
    // Setup app hosts / local setup ports commonly resolve unconfigured hashes
    // to `/{hash}` — treat as needs_setup when not an app or profile host.
    const appLike =
      host === 'app.up2promo.com' ||
      host.includes('.forwarder.') ||
      host === 'up2.page' ||
      host.startsWith('link.');
    if (appLike) return false;
    return true;
  } catch {
    return false;
  }
}

function workspaceEditableProductId(
  ownership: ProductHashResolution | null | undefined,
): string | undefined {
  if (
    ownership?.kind === 'already_in_workspace' ||
    ownership?.kind === 'assigned_to_other_user'
  ) {
    return ownership.productId;
  }
  return undefined;
}

/**
 * Public destinations guests can open. App / product link / setup hosts are
 * not treated as live product destinations on the casual scan path.
 */
function openablePublicTargetUrl(targetUrl: string): string | null {
  try {
    const classified = classifyNativeLink(new URL(targetUrl));
    if (classified.kind === 'app' || classified.kind === 'product') {
      return null;
    }
  } catch {
    if (isSetupFrontendUrl(targetUrl)) {
      return null;
    }
  }
  return targetUrl;
}

function visitorDestination(params: {
  hash: string | null;
  targetUrl: string;
  mappingType: ProductBehavior | null;
  productType: LegacyProductCode | null;
}): LinkResolveDestination {
  return {
    kind: 'visitor_destination',
    hash: params.hash,
    targetUrl: openablePublicTargetUrl(params.targetUrl),
    mappingType: params.mappingType,
    productType: params.productType,
  };
}

/**
 * Maps public resolve (+ optional ownership) into a single dialog destination.
 *
 * Entry source: this classifier feeds the **deeplink / scan / NFC** dialog.
 * Claim / resetup CTAs must not be primary here — those stay on the intentional
 * Products → Add / Connect wizard (`ProductHashOutcomePanel`).
 */
export function classifyLinkResolve(
  input: ClassifyLinkResolveInput,
): LinkResolveDestination {
  const { hash, resolution, ownership, directProfile, hintProductType } = input;

  if (directProfile?.workspaceUsername && directProfile.username) {
    return {
      kind: 'profile_card',
      hash,
      targetUrl: directProfile.profileUrl ?? null,
      mappingType: ProductBehavior.PROFILE,
      productType: LegacyProductCode.BC,
      profile: {
        workspaceUsername: directProfile.workspaceUsername,
        username: directProfile.username,
      },
      productId: workspaceEditableProductId(ownership),
    };
  }

  if (!resolution?.targetUrl) {
    return {
      kind: 'error_unavailable',
      hash,
      targetUrl: null,
      mappingType: resolution?.kind ?? null,
      productType:
        mergeSetupProductType(
          ownership?.productType,
          resolution?.legacyCode,
          hintProductType,
        ) ?? null,
    };
  }

  const targetUrl = resolution.targetUrl;
  const mappingType = resolution.kind ?? null;
  // Ownership / resolve DB type wins; QR `t` fills when DB has none.
  const productType =
    mergeSetupProductType(
      ownership?.productType,
      resolution.legacyCode,
      hintProductType,
    ) ?? null;

  // Public profile / BC destinations win over ownership — another team's card
  // is still a profile_card for exchange, not "other workspace". Same-workspace
  // ownership still attaches productId so owners can edit the product.
  const profile = parseProfileViewerUrl(targetUrl);
  if (isProfileScan(mappingType, productType) && profile) {
    return {
      kind: 'profile_card',
      hash,
      targetUrl,
      mappingType,
      productType,
      profile,
      productId: workspaceEditableProductId(ownership),
    };
  }

  // Another workspace's live product (common for store visitors who also use
  // up2promo). Treat as a normal public destination — not a setup/error path.
  if (ownership?.kind === 'other_workspace') {
    return visitorDestination({
      hash,
      targetUrl,
      mappingType,
      productType:
        mergeSetupProductType(ownership.productType, productType) ?? null,
    });
  }

  if (ownership?.kind === 'already_in_workspace' && ownership.productId) {
    return {
      kind: 'owned_product',
      hash,
      targetUrl,
      mappingType,
      productType:
        mergeSetupProductType(ownership.productType, productType) ?? null,
      productId: ownership.productId,
    };
  }

  if (ownership?.kind === 'assigned_to_other_user') {
    return {
      kind: 'teammate_product',
      hash,
      targetUrl,
      mappingType,
      productType:
        mergeSetupProductType(ownership.productType, productType) ?? null,
      productId: ownership.productId,
    };
  }

  // Already configured (unowned / claimable): casual open → use / view only.
  // Claim & connect remain on Products → Add product / Connect product.
  if (
    ownership?.kind === 'send_claim_email' ||
    ownership?.kind === 'claim_manual' ||
    ownership?.kind === 'claim_pending'
  ) {
    return visitorDestination({
      hash,
      targetUrl,
      mappingType,
      productType:
        mergeSetupProductType(ownership.productType, productType) ?? null,
    });
  }

  // Blank / factory hash — setup is appropriate on this path.
  if (ownership?.kind === 'ready_for_setup') {
    return {
      kind: 'needs_setup',
      hash,
      targetUrl,
      mappingType,
      productType:
        mergeSetupProductType(ownership.productType, productType) ?? null,
      productId: ownership.productId,
    };
  }

  try {
    const classified = classifyNativeLink(new URL(targetUrl));
    if (classified.kind === 'app') {
      const path = ensureSetupPathHasProductType(classified.path, productType);
      if (path.startsWith('/link-error')) {
        return {
          kind: 'error_unavailable',
          hash,
          targetUrl,
          mappingType,
          productType,
          appPath: path,
        };
      }
      if (
        path.includes('/products/setup') ||
        path.includes('/profiles/link-card')
      ) {
        return {
          kind: 'needs_setup',
          hash,
          targetUrl,
          mappingType,
          productType,
          appPath: path,
        };
      }
      return {
        kind: 'app_path',
        hash,
        targetUrl,
        mappingType,
        productType,
        appPath: path,
      };
    }
    if (classified.kind === 'profile') {
      return {
        kind: 'profile_card',
        hash,
        targetUrl,
        mappingType: mappingType ?? ProductBehavior.PROFILE,
        productType,
        profile: {
          workspaceUsername: classified.workspaceUsername,
          username: classified.username,
        },
        productId: workspaceEditableProductId(ownership),
      };
    }
  } catch {
    // fall through
  }

  if (isSetupFrontendUrl(targetUrl)) {
    return {
      kind: 'needs_setup',
      hash,
      targetUrl,
      mappingType,
      productType,
    };
  }

  return {
    kind: 'external',
    hash,
    targetUrl,
    mappingType,
    productType,
  };
}
